/**
 * String normalization & fuzzy matching.
 * Preserved from v1 — battle-tested edge case handling.
 */

export function toGraphKey(s) {
  if (!s) return '';
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[.,…'?/#!$%^*;:{}=_`~()]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .replace(/^(the-|a-)/, '')
    .replace(/-the-/g, '-')
    .replace(/^([a-z])-([a-z])-/, (_, a, b) => `${a}${b}-`);
}

export function numToWords(num) {
  const underTwenty = ['zero','one','two','three','four','five','six','seven','eight','nine','ten','eleven','twelve','thirteen','fourteen','fifteen','sixteen','seventeen','eighteen','nineteen'];
  const tens = ['twenty','thirty','forty','fifty','sixty','seventy','eighty','ninety'];
  const thousands = ['thousand','million','billion','trillion','quadrillion'];
  function twoDigit(n) { if (n < 20) return underTwenty[n]; if (n % 10 === 0) return tens[Math.floor(n/10)-2]; return `${tens[Math.floor(n/10)-2]} ${underTwenty[n%10]}`; }
  function threeDigit(n) { const rem = n % 100; const out = [`${underTwenty[Math.floor(n/100)]} hundred`]; if (rem !== 0) out.push(twoDigit(rem)); return out.join(' '); }
  function underThousand(n) { return n < 100 ? twoDigit(n) : threeDigit(n); }
  if (num < 1000) return underThousand(num);
  const batches = []; let temp = num;
  while (temp > 0) { batches.push(temp % 1000); temp = Math.floor(temp / 1000); }
  const out = [];
  for (let i = batches.length - 1; i >= 0; i--) { if (batches[i] !== 0) { out.push(underThousand(batches[i])); if (i > 0) out.push(thousands[i - 1]); } }
  return out.join(' ');
}

export function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n; if (n === 0) return m;
  const d = Array.from({ length: m + 1 }, (_, i) => [i]);
  for (let j = 1; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      d[i][j] = a[i - 1] === b[j - 1] ? d[i - 1][j - 1] : 1 + Math.min(d[i - 1][j], d[i][j - 1], d[i - 1][j - 1]);
  return d[m][n];
}

export function jaroWinkler(a, b) {
  if (a === b) return 1;
  const al = a.length, bl = b.length;
  if (!al || !bl) return 0;
  const range = Math.max(0, Math.floor(Math.max(al, bl) / 2) - 1);
  const aMatches = new Array(al).fill(false), bMatches = new Array(bl).fill(false);
  let matches = 0, transpositions = 0;
  for (let i = 0; i < al; i++) {
    const lo = Math.max(0, i - range), hi = Math.min(bl - 1, i + range);
    for (let j = lo; j <= hi; j++) { if (bMatches[j] || a[i] !== b[j]) continue; aMatches[i] = bMatches[j] = true; matches++; break; }
  }
  if (!matches) return 0;
  let k = 0;
  for (let i = 0; i < al; i++) { if (!aMatches[i]) continue; while (!bMatches[k]) k++; if (a[i] !== b[k]) transpositions++; k++; }
  const jaro = (matches / al + matches / bl + (matches - transpositions / 2) / matches) / 3;
  let prefix = 0;
  for (let i = 0; i < Math.min(4, al, bl); i++) { if (a[i] === b[i]) prefix++; else break; }
  return jaro + prefix * 0.1 * (1 - jaro);
}

export function fuzzyMatch(input, candidates) {
  if (!input || !candidates.length) return null;
  const n = input.toLowerCase();
  let best = null, bestScore = 0;
  for (const c of candidates) {
    const cn = c.toLowerCase();
    if (cn === n) return { match: c, score: 1 };
    const maxLen = Math.max(n.length, cn.length);
    if (!maxLen) continue;
    const levScore = 1 - levenshtein(n, cn) / maxLen;
    const jwScore = jaroWinkler(n, cn);
    const score = (levScore >= 0.75 ? levScore : 0) || (jwScore >= 0.95 ? jwScore : 0);
    if (score > bestScore) { bestScore = score; best = c; }
  }
  return bestScore > 0 ? { match: best, score: bestScore } : null;
}

export function toGraphKeyNoInitials(s) {
  if (!s) return '';
  const parts = s.trim().split(/\s+/);
  if (parts.length <= 2) return null;
  const filtered = [parts[0]];
  for (let i = 1; i < parts.length - 1; i++) { const p = parts[i].replace(/\.$/, ''); if (p.length === 1) continue; filtered.push(parts[i]); }
  filtered.push(parts[parts.length - 1]);
  if (filtered.length === parts.length) return null;
  return toGraphKey(filtered.join(' '));
}

export function toGraphKeyNumWords(s) {
  const words = s.split(' ');
  let changed = false;
  const result = words.map(w => { const n = parseInt(w, 10); if (Number.isSafeInteger(n) && String(n) === w) { changed = true; return numToWords(n); } return w; });
  return changed ? toGraphKey(result.join(' ')) : null;
}
